import { z } from 'zod';
import { financeExpenseCategorySchema, financeExpenseIdSchema, financeExpensePaymentMethodSchema, financeExpenseSchema, financeExpenseScopeSchema, } from './finance-expense.contract.js';
import { financeDateOnlySchema, financeInvoiceCommandIdSchema, financeMoneyCentsSchema, } from './finance-invoice.contract.js';
export const financeReconciliationCapability = 'finance.reconciliation.v1';
export const financeReconciliationIdSchema = z.string().uuid();
export const financeReconciliationSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const financeReconciliationSourceTypeSchema = z.enum([
    'bank-statement', 'card-statement', 'processor-report', 'manual',
]);
export const financeReconciliationStatementStatusSchema = z.enum([
    'ready', 'in-review', 'reconciled', 'closed', 'failed',
]);
export const financeReconciliationDirectionSchema = z.enum(['debit', 'credit']);
export const financeReconciliationTransactionKindSchema = z.enum([
    'purchase', 'payment', 'deposit', 'refund', 'fee', 'transfer', 'other',
]);
export const financeReconciliationMatchStatusSchema = z.enum([
    'unmatched', 'suggested', 'auto-matched', 'manual-matched', 'no-receipt', 'ignored',
]);
export const financeReconciliationTargetTypeSchema = z.enum([
    'expense', 'payment', 'bill-occurrence',
]);
export const financeReconciliationSignalSchema = z.enum([
    'amount-exact', 'date-one-day', 'date-three-days', 'date-seven-days',
    'card-exact', 'reference-exact', 'resource-eligible',
]);
const nullableShortText = z.string().trim().min(1).max(240).nullable();
const nullableLongText = z.string().trim().min(1).max(2_000).nullable();
export const financeReconciliationNormalizedRowSchema = z.strictObject({
    transactionId: financeReconciliationIdSchema,
    rowNumber: z.number().int().min(1).max(1_000_000),
    postedDate: financeDateOnlySchema,
    direction: financeReconciliationDirectionSchema,
    kind: financeReconciliationTransactionKindSchema,
    description: z.string().trim().min(1).max(1_000),
    counterparty: nullableShortText.default(null),
    amountCents: financeMoneyCentsSchema.min(1),
    accountLastFour: z.string().regex(/^\d{4}$/).nullable().default(null),
    employeeLabel: nullableShortText.default(null),
    externalReference: nullableShortText.default(null),
    fingerprint: financeReconciliationSha256Schema,
});
export const financeReconciliationStatementImportCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    statementId: financeReconciliationIdSchema,
    sourceType: financeReconciliationSourceTypeSchema,
    sourceLabel: z.string().trim().min(1).max(120),
    sourceReference: nullableShortText.default(null),
    accountName: z.string().trim().min(1).max(200),
    accountLastFour: z.string().regex(/^\d{4}$/).nullable().default(null),
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
    currencyCode: z.literal('USD'),
    openingBalanceCents: financeMoneyCentsSchema.nullable().default(null),
    closingBalanceCents: financeMoneyCentsSchema.nullable().default(null),
    rows: z.array(financeReconciliationNormalizedRowSchema).min(1).max(5_000),
    reason: z.string().trim().min(1).max(2_000),
}).superRefine((value, context) => {
    if (value.periodEnd < value.periodStart) {
        context.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Period order is invalid.' });
    }
    const rowNumbers = new Set();
    const transactionIds = new Set();
    const fingerprints = new Set();
    for (const [index, row] of value.rows.entries()) {
        if (row.postedDate < value.periodStart || row.postedDate > value.periodEnd) {
            context.addIssue({
                code: 'custom', path: ['rows', index, 'postedDate'], message: 'Row is outside the period.',
            });
        }
        if (rowNumbers.has(row.rowNumber)) {
            context.addIssue({ code: 'custom', path: ['rows', index, 'rowNumber'], message: 'Duplicate row identity.' });
        }
        if (transactionIds.has(row.transactionId)) {
            context.addIssue({ code: 'custom', path: ['rows', index, 'transactionId'], message: 'Duplicate row identity.' });
        }
        if (fingerprints.has(row.fingerprint)) {
            context.addIssue({ code: 'custom', path: ['rows', index, 'fingerprint'], message: 'Duplicate row identity.' });
        }
        rowNumbers.add(row.rowNumber);
        transactionIds.add(row.transactionId);
        fingerprints.add(row.fingerprint);
    }
});
export const financeReconciliationCandidateSchema = z.strictObject({
    id: financeReconciliationIdSchema,
    transactionId: financeReconciliationIdSchema,
    targetType: financeReconciliationTargetTypeSchema,
    targetId: financeReconciliationIdSchema,
    targetRevision: z.number().int().positive(),
    targetDate: financeDateOnlySchema,
    targetLabel: z.string().trim().min(1).max(300),
    targetAmountCents: financeMoneyCentsSchema,
    scoreBasisPoints: z.number().int().min(0).max(10_000),
    signals: z.array(financeReconciliationSignalSchema).min(1).max(10),
    createdAt: z.string().datetime({ offset: true }),
});
export const financeReconciliationMatchedTargetSchema = z.strictObject({
    type: financeReconciliationTargetTypeSchema,
    id: financeReconciliationIdSchema,
    label: z.string().trim().min(1).max(300),
    amountCents: financeMoneyCentsSchema,
    revision: z.number().int().positive(),
});
export const financeReconciliationReceiptRequestIntentSchema = z.strictObject({
    contractVersion: z.literal(financeReconciliationCapability),
    tenantId: z.string().uuid(),
    id: financeReconciliationIdSchema,
    transactionId: financeReconciliationIdSchema,
    statementId: financeReconciliationIdSchema,
    transactionRevision: z.number().int().positive(),
    recipientMembershipId: financeReconciliationIdSchema,
    requestedByMembershipId: financeReconciliationIdSchema,
    message: nullableLongText,
    reason: z.string().trim().min(1).max(2_000),
    preparedAt: z.string().datetime({ offset: true }),
});
export const financeReconciliationTransactionSchema = z.strictObject({
    contractVersion: z.literal(financeReconciliationCapability),
    tenantId: z.string().uuid(),
    id: financeReconciliationIdSchema,
    statementId: financeReconciliationIdSchema,
    rowNumber: z.number().int().positive(),
    revision: z.number().int().positive(),
    postedDate: financeDateOnlySchema,
    direction: financeReconciliationDirectionSchema,
    kind: financeReconciliationTransactionKindSchema,
    description: z.string().trim().min(1).max(1_000),
    counterparty: nullableShortText,
    amountCents: financeMoneyCentsSchema.min(1),
    accountLastFour: z.string().regex(/^\d{4}$/).nullable(),
    employeeLabel: nullableShortText,
    externalReference: nullableShortText,
    fingerprint: financeReconciliationSha256Schema,
    matchStatus: financeReconciliationMatchStatusSchema,
    matchConfidenceBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    matchedTarget: financeReconciliationMatchedTargetSchema.nullable(),
    resolutionReason: nullableLongText,
    resolvedByMembershipId: financeReconciliationIdSchema.nullable(),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
    candidates: z.array(financeReconciliationCandidateSchema).max(100),
    receiptRequestIntents: z.array(financeReconciliationReceiptRequestIntentSchema).max(100).default([]),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    const matched = value.matchStatus === 'auto-matched' || value.matchStatus === 'manual-matched';
    if (matched !== (value.matchedTarget !== null)) {
        context.addIssue({ code: 'custom', path: ['matchedTarget'], message: 'Matched target state is invalid.' });
    }
    const resolved = matched || value.matchStatus === 'no-receipt' || value.matchStatus === 'ignored';
    if (resolved !== (value.resolvedByMembershipId !== null && value.resolvedAt !== null)) {
        context.addIssue({ code: 'custom', path: ['resolvedAt'], message: 'Resolution evidence is incomplete.' });
    }
});
export const financeReconciliationStatementSummarySchema = z.strictObject({
    transactionCount: z.number().int().nonnegative(),
    debitCount: z.number().int().nonnegative(),
    creditCount: z.number().int().nonnegative(),
    debitCents: financeMoneyCentsSchema,
    creditCents: financeMoneyCentsSchema,
    matchedCount: z.number().int().nonnegative(),
    noReceiptCount: z.number().int().nonnegative(),
    ignoredCount: z.number().int().nonnegative(),
    unresolvedCount: z.number().int().nonnegative(),
}).superRefine((value, context) => {
    if (value.transactionCount !== value.debitCount + value.creditCount) {
        context.addIssue({ code: 'custom', path: ['transactionCount'], message: 'Direction counts do not reconcile.' });
    }
    if (value.transactionCount !==
        value.matchedCount + value.noReceiptCount + value.ignoredCount + value.unresolvedCount) {
        context.addIssue({ code: 'custom', path: ['unresolvedCount'], message: 'Resolution counts do not reconcile.' });
    }
});
export const financeReconciliationStatementSchema = z.strictObject({
    contractVersion: z.literal(financeReconciliationCapability),
    tenantId: z.string().uuid(),
    id: financeReconciliationIdSchema,
    revision: z.number().int().positive(),
    sourceType: financeReconciliationSourceTypeSchema,
    sourceLabel: z.string().trim().min(1).max(120),
    sourceReference: nullableShortText,
    accountName: z.string().trim().min(1).max(200),
    accountLastFour: z.string().regex(/^\d{4}$/).nullable(),
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
    currencyCode: z.literal('USD'),
    openingBalanceCents: financeMoneyCentsSchema.nullable(),
    closingBalanceCents: financeMoneyCentsSchema.nullable(),
    status: financeReconciliationStatementStatusSchema,
    contentHash: financeReconciliationSha256Schema,
    importedByMembershipId: financeReconciliationIdSchema,
    reason: z.string().trim().min(1).max(2_000),
    summary: financeReconciliationStatementSummarySchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const financeReconciliationStatementDetailSchema = z.strictObject({
    statement: financeReconciliationStatementSchema,
    transactions: z.array(financeReconciliationTransactionSchema).max(5_000),
}).superRefine((value, context) => {
    if (value.statement.summary.transactionCount !== value.transactions.length) {
        context.addIssue({ code: 'custom', path: ['transactions'], message: 'Statement rows do not reconcile.' });
    }
});
export const financeReconciliationStatementImportResponseSchema = z.strictObject({
    data: financeReconciliationStatementDetailSchema,
    command: z.strictObject({ commandId: financeInvoiceCommandIdSchema, idempotent: z.boolean() }),
});
export const financeReconciliationTransactionCommandSchema = z.discriminatedUnion('action', [
    z.strictObject({
        action: z.literal('accept-candidate'),
        commandId: financeInvoiceCommandIdSchema,
        expectedRevision: z.number().int().positive(),
        candidateId: financeReconciliationIdSchema,
        reason: z.string().trim().min(1).max(2_000),
    }),
    z.strictObject({
        action: z.literal('match'),
        commandId: financeInvoiceCommandIdSchema,
        expectedRevision: z.number().int().positive(),
        targetType: financeReconciliationTargetTypeSchema,
        targetId: financeReconciliationIdSchema,
        reason: z.string().trim().min(1).max(2_000),
    }),
    z.strictObject({
        action: z.enum(['no-receipt', 'ignore', 'reopen']),
        commandId: financeInvoiceCommandIdSchema,
        expectedRevision: z.number().int().positive(),
        reason: z.string().trim().min(1).max(2_000),
    }),
]);
export const financeReconciliationStatementCommandSchema = z.strictObject({
    action: z.enum(['start-review', 'close', 'reopen']),
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeReconciliationCreateExpenseCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expenseId: financeExpenseIdSchema,
    expectedTransactionRevision: z.number().int().positive(),
    scope: financeExpenseScopeSchema,
    projectId: z.string().uuid().nullable().default(null),
    vendor: z.string().trim().min(1).max(300).nullable().default(null),
    description: z.string().trim().min(1).max(2_000).nullable().default(null),
    category: financeExpenseCategorySchema,
    paymentMethod: financeExpensePaymentMethodSchema,
    costCenterCode: z.string().trim().min(1).max(50).nullable().default(null),
    accountCategoryCode: z.string().trim().min(1).max(80).nullable().default(null),
    reason: z.string().trim().min(1).max(2_000),
}).superRefine((value, context) => {
    if ((value.scope === 'job') !== (value.projectId !== null)) {
        context.addIssue({
            code: 'custom', path: ['projectId'],
            message: 'Only a job expense requires and accepts a Project reference.',
        });
    }
});
export const financeReconciliationReceiptRequestCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    intentId: financeReconciliationIdSchema,
    expectedTransactionRevision: z.number().int().positive(),
    recipientMembershipId: financeReconciliationIdSchema,
    message: nullableLongText.default(null),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeReconciliationTransactionCommandResponseSchema = z.strictObject({
    data: financeReconciliationTransactionSchema,
    statement: financeReconciliationStatementSchema,
    command: z.strictObject({ commandId: financeInvoiceCommandIdSchema, idempotent: z.boolean() }),
});
export const financeReconciliationStatementCommandResponseSchema = z.strictObject({
    data: financeReconciliationStatementSchema,
    command: z.strictObject({ commandId: financeInvoiceCommandIdSchema, idempotent: z.boolean() }),
});
export const financeReconciliationCreateExpenseResponseSchema = z.strictObject({
    data: z.strictObject({
        expense: financeExpenseSchema,
        transaction: financeReconciliationTransactionSchema,
        statement: financeReconciliationStatementSchema,
    }),
    command: z.strictObject({ commandId: financeInvoiceCommandIdSchema, idempotent: z.boolean() }),
});
export const financeReconciliationReceiptRequestResponseSchema = z.strictObject({
    data: financeReconciliationReceiptRequestIntentSchema,
    command: z.strictObject({ commandId: financeInvoiceCommandIdSchema, idempotent: z.boolean() }),
});
export const financeReconciliationStatementListQuerySchema = z.strictObject({
    status: financeReconciliationStatementStatusSchema.optional(),
    sourceType: financeReconciliationSourceTypeSchema.optional(),
    periodFrom: financeDateOnlySchema.optional(),
    periodTo: financeDateOnlySchema.optional(),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).max(1_000_000).default(0),
});
const pageSchema = z.strictObject({
    limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
export const financeReconciliationStatementListResponseSchema = z.strictObject({
    data: z.array(financeReconciliationStatementSchema),
    page: pageSchema,
});
export const financeReconciliationDashboardSchema = z.strictObject({
    contractVersion: z.literal(financeReconciliationCapability),
    statementCount: z.number().int().nonnegative(),
    transactionCount: z.number().int().nonnegative(),
    matchedCount: z.number().int().nonnegative(),
    unresolvedCount: z.number().int().nonnegative(),
    noReceiptCount: z.number().int().nonnegative(),
    ignoredCount: z.number().int().nonnegative(),
    debitCents: financeMoneyCentsSchema,
    creditCents: financeMoneyCentsSchema,
    matchRateBasisPoints: z.number().int().min(0).max(10_000),
});
export function scoreFinanceReconciliationCandidate(input) {
    if (input.transactionAmountCents !== input.targetAmountCents)
        return null;
    const dayMs = 86_400_000;
    const days = Math.round(Math.abs(new Date(`${input.transactionDate}T00:00:00Z`).getTime() -
        new Date(`${input.targetDate}T00:00:00Z`).getTime()) / dayMs);
    const cardExact = input.transactionCardLastFour !== null &&
        input.transactionCardLastFour === input.targetCardLastFour;
    if (cardExact && days <= 1) {
        return { scoreBasisPoints: 10_000, signals: ['amount-exact', 'card-exact', 'date-one-day', 'resource-eligible'] };
    }
    if (days <= 3) {
        return { scoreBasisPoints: 8_000, signals: ['amount-exact', 'date-three-days', 'resource-eligible'] };
    }
    return { scoreBasisPoints: 6_000, signals: ['amount-exact', 'resource-eligible'] };
}
//# sourceMappingURL=finance-reconciliation.contract.js.map